import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowUpRight, Newspaper } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { useLang } from "@/lib/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function News() {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/news`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const locale = lang === "en" ? "en-GB" : "it-IT";
  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });

  const excerpt = (body) => body.replace(/\s+/g, " ").slice(0, 160) + (body.length > 160 ? "…" : "");

  return (
    <section data-testid="news-section" id="news" className="border-t border-hairline bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <Overline>{t.news.overline}</Overline>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-navy sm:text-4xl lg:text-5xl">
              {t.news.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-md text-sm leading-relaxed text-muted-ink lg:text-base">{t.news.intro}</p>
          </Reveal>
        </div>

        {items === null && (
          <p className="mt-14 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-ink">…</p>
        )}

        {items !== null && items.length === 0 && (
          <Reveal>
            <div data-testid="news-empty" className="mt-14 flex flex-col items-center gap-3 border border-hairline bg-alabaster px-8 py-16 text-center">
              <Newspaper size={30} strokeWidth={1.1} className="text-gold-dark" />
              <p className="mt-2 font-serif text-2xl font-medium text-navy">{t.news.empty}</p>
              <p className="text-sm text-muted-ink">{t.news.emptyHint}</p>
            </div>
          </Reveal>
        )}

        {items !== null && items.length > 0 && (
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {items.slice(0, 3).map((n, i) => (
              <Reveal key={n.id} delay={0.06 * i}>
                <button
                  data-testid={`news-card-${n.id}`}
                  onClick={() => setSelected(n)}
                  className="group flex h-full w-full flex-col border border-hairline bg-alabaster p-8 text-left transition-all duration-500 hover:-translate-y-1 hover:border-gold hover:shadow-[0_24px_60px_-30px_rgba(11,29,58,0.35)]"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-dark">{fmtDate(n.created_at)}</span>
                  <h3 className="mt-4 font-serif text-2xl font-semibold leading-snug text-navy">{n.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-ink">{excerpt(n.body)}</p>
                  <span className="link-gold mt-6 inline-flex w-max items-center gap-2 self-start font-mono text-[10px] uppercase tracking-[0.2em] text-navy">
                    {t.news.readMore}
                    <ArrowUpRight size={14} strokeWidth={1.25} className="text-gold-dark transition-transform duration-500 group-hover:translate-x-0.5" />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl overflow-y-auto max-h-[85vh] border-hairline bg-alabaster p-0" data-testid="news-modal">
          {selected && (
            <DialogHeader className="p-8 text-left lg:p-10">
              <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-dark">
                {fmtDate(selected.created_at)} · {FIRM_SHORT}
              </DialogDescription>
              <DialogTitle className="mt-2 font-serif text-3xl font-semibold leading-snug text-navy sm:text-4xl">
                {selected.title}
              </DialogTitle>
              <p data-testid="news-modal-body" className="mt-6 whitespace-pre-line text-sm leading-relaxed text-muted-ink sm:text-base">
                {selected.body}
              </p>
            </DialogHeader>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

const FIRM_SHORT = "Legalia — Società tra Avvocati";
